import React, { useState, useEffect, useRef } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { showToast } from "../../../utils/customToast";
import "./UpdateLedger.css";
import { apiWithLog } from "../../../utils/apiWithLog";

function AddLedgerMaster() {
  const navigate = useNavigate();
  const location = useLocation();

  const [loading, setLoading] = useState(false);
  const [groups, setGroups] = useState([]);
  const [states, setStates] = useState([]);

  // Company State
  const [selectedCompID, setSelectedCompID] = useState("");
  const [selectedCompanyName, setSelectedCompanyName] = useState("");

  const BASE_API_URL = process.env.REACT_APP_BASE_API_URL;

  const [formData, setFormData] = useState({
    HEADName: "",
    GroupCODE: "",
    StateID: "",
    OpeningBal: "",
    OpenBlType: "Dr",
    BillbyBill: false,
    CL_BALANCE: "",
    GSTNO: "",
    PANNO: "",
    MOBILE: "",
    PHONE: "",
    EMAIL: "",
    ADDRESS1: "",
    ADDRESS2: "",
  });

  const lastCompIDRef = useRef(null);

  useEffect(() => {
    const syncCompany = () => {
      const company = JSON.parse(localStorage.getItem("selectedCompany"));
      const compId = company?.id || "";
      const compName = company?.name || "";

      if (compId && lastCompIDRef.current !== compId) {
        lastCompIDRef.current = compId;
        setSelectedCompID(compId);
        setSelectedCompanyName(compName);
      }
    };

    syncCompany();

    const interval = setInterval(syncCompany, 500);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const fetchDropdowns = async () => {
      setLoading(true);
      try {
        const response = await fetch(`${BASE_API_URL}/ledger/dropdowns`);
        const data = await response.json();

        if (data.success) {
          const loadedGroups =
            data.groups?.sort((a, b) =>
              a.GroupNAME.localeCompare(b.GroupNAME, undefined, { numeric: true })
            ) || [];
          setGroups(loadedGroups);
          setStates(
            data.states?.sort((a, b) =>
              a.name.localeCompare(b.name, undefined, { numeric: true })
            ) || []
          );

          // Pre-select group if arriving from Add Vehicle (default: Sundry Creditors)
          const urlParams = new URLSearchParams(location.search);
          const defaultGrp = location.state?.defaultGroup || urlParams.get("group");
          const fromVehicle = location.state?.fromVehicle;

          let targetGroupCode = "";
          if (defaultGrp) {
            targetGroupCode = String(defaultGrp);
          } else if (fromVehicle) {
            const creditorGroup = loadedGroups.find(
              (g) => g.GroupCODE === 23 || /sundry\s*creditor/i.test(g.GroupNAME || "")
            );
            if (creditorGroup) targetGroupCode = String(creditorGroup.GroupCODE);
          }

          if (targetGroupCode) {
            setFormData((prev) => ({
              ...prev,
              GroupCODE: targetGroupCode,
            }));
          }
        } else {
          showToast.error("Failed to load dropdown data");
        }
      } catch (err) {
        console.error("Dropdown Fetch Error:", err);
        showToast.error("Failed to load dropdown data");
      } finally {
        setLoading(false);
      }
    };

    fetchDropdowns();
  }, [BASE_API_URL]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    let newValue = type === "checkbox" ? checked : value;

    if (name === "GSTNO" || name === "PANNO") {
      newValue = newValue.toUpperCase();
    }

    setFormData((prev) => ({
      ...prev,
      [name]: newValue,
    }));
  };

  const validateGST = () => {
    if (!formData.GSTNO) return true;
    const gst = formData.GSTNO.toUpperCase();

    if (gst.length !== 15) {
      showToast.error("GST number must be exactly 15 characters.");
      return false;
    }

    const selectedState = states.find(
      (state) => String(state.id) === String(formData.StateID)
    );
    if (!selectedState) {
      showToast.error("Please select a valid State before entering GST.");
      return false;
    }

    const gstCode = gst.substring(0, 2);
    const expectedCode = String(selectedState.code).padStart(2, "0");
    if (gstCode !== expectedCode) {
      showToast.error(
        `GST number must start with "${expectedCode}" for state "${selectedState.name}".`
      );
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;

    if (!selectedCompID) {
      showToast.error("Company not selected! Please select a company first.");
      return;
    }

    if (!formData.HEADName?.trim()) {
      showToast.warn("Please enter a ledger name.");
      return;
    }

    if (!formData.GroupCODE) {
      showToast.warn("Please select a group.");
      return;
    }

    if (!formData.StateID) {
      showToast.warn("Please select a state.");
      return;
    }

    if (!validateGST()) {
      return;
    }

    try {
      setLoading(true);

      const cleanStr = (s) => (s ? String(s).replace(/^["'“”`\s]+|["'“”`\s]+$/g, "").trim() : "");
      const payload = {
        CompID: selectedCompID,
        HEADName: cleanStr(formData.HEADName),
        GroupCODE: parseInt(formData.GroupCODE, 10),
        StateID: parseInt(formData.StateID, 10),
        OpeningBal: Number(formData.OpeningBal || 0),
        OpenBlType: formData.OpenBlType || "Dr",
        BillbyBill: formData.BillbyBill ? 1 : 0,
        CL_BALANCE: Number(formData.CL_BALANCE || 0),
        GSTNO: formData.GSTNO ? cleanStr(formData.GSTNO).toUpperCase() : "",
        PANNO: formData.PANNO ? cleanStr(formData.PANNO).toUpperCase() : "",
        MOBILE: cleanStr(formData.MOBILE),
        PHONE: cleanStr(formData.PHONE),
        EMAIL: cleanStr(formData.EMAIL),
        ADDRESS1: cleanStr(formData.ADDRESS1),
        ADDRESS2: cleanStr(formData.ADDRESS2),
      };

      const res = await apiWithLog({
        method: "POST",
        url: `${BASE_API_URL}/ledger/create`,
        data: payload,
        module: "LedgerMaster",
        action: "A",
        headers: {
          "Content-Type": "application/json",
        },
      });

      const result = res.data || {};

      if (res.status >= 200 && res.status < 300) {
        showToast.save(`Ledger saved for ${selectedCompanyName || "company"}`);
        setTimeout(() => {
          navigate("/ShowLedger");
        }, 1200);
      } else {
        showToast.error(result.message || result.error || "Failed to add ledger");
      }
    } catch (err) {
      console.error("Ledger Add Error:", err);
      showToast.error(
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        "Failed to add ledger"
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="update_ledger_container">
      {/* HEADER & BREADCRUMBS */}
      <div className="update_ledger_header_bar">
        <div className="update_ledger_title_group">
          <h6 className="update_ledger_title">Add Ledger</h6>
          <div className="update_ledger_breadcrumbs">
            <Link to="/">Dashboard</Link>
            <span>&gt;</span>
            <Link to="/ShowLedger">Ledger Master</Link>
            <span>&gt;</span>
            <span>Add Ledger</span>
          </div>
        </div>
      </div>

      {/* FORM CARD */}
      <div className="update_ledger_card">
        <div className="update_ledger_card_header">
          <h5>Create New Ledger {selectedCompanyName ? `(${selectedCompanyName})` : ""}</h5>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="update_ledger_grid">
            {/* Ledger Name */}
            <div className="update_ledger_field span_2">
              <label htmlFor="HEADName">
                Ledger Name <span className="required">*</span>
              </label>
              <input
                id="HEADName"
                type="text"
                name="HEADName"
                placeholder="Enter ledger name"
                value={formData.HEADName}
                onChange={handleChange}
                required
              />
            </div>

            {/* Group Dropdown */}
            <div className="update_ledger_field">
              <label htmlFor="GroupCODE">
                Group Name <span className="required">*</span>
              </label>
              <select
                id="GroupCODE"
                name="GroupCODE"
                value={formData.GroupCODE}
                onChange={handleChange}
                required
              >
                <option value="">-- Select Group --</option>
                {groups.map((g) => (
                  <option key={g.GroupCODE} value={String(g.GroupCODE)}>
                    {g.GroupNAME}
                  </option>
                ))}
              </select>
            </div>

            {/* State Dropdown */}
            <div className="update_ledger_field">
              <label htmlFor="StateID">
                State <span className="required">*</span>
              </label>
              <select
                id="StateID"
                name="StateID"
                value={formData.StateID}
                onChange={handleChange}
                required
              >
                <option value="">-- Select State --</option>
                {states.map((state) => (
                  <option key={state.id} value={String(state.id)}>
                    {state.name} ({state.code})
                  </option>
                ))}
              </select>
            </div>

            {/* Opening Balance */}
            <div className="update_ledger_field">
              <label htmlFor="OpeningBal">Opening Balance</label>
              <input
                id="OpeningBal"
                type="number"
                step="any"
                name="OpeningBal"
                placeholder="0.00"
                value={formData.OpeningBal}
                onChange={handleChange}
              />
            </div>

            {/* Open Balance Type */}
            <div className="update_ledger_field">
              <label htmlFor="OpenBlType">Opening Balance Type</label>
              <select
                id="OpenBlType"
                name="OpenBlType"
                value={formData.OpenBlType}
                onChange={handleChange}
                required
              >
                <option value="Dr">Dr (Debit)</option>
                <option value="Cr">Cr (Credit)</option>
              </select>
            </div>

            {/* Bill by Bill */}
            <div className="update_ledger_field">
              <label htmlFor="BillbyBill">Bill by Bill</label>
              <select
                id="BillbyBill"
                name="BillbyBill"
                value={formData.BillbyBill ? "1" : "0"}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    BillbyBill: e.target.value === "1",
                  }))
                }
              >
                <option value="0">No</option>
                <option value="1">Yes</option>
              </select>
            </div>

            {/* Closing Balance */}
            <div className="update_ledger_field">
              <label htmlFor="CL_BALANCE">Closing Balance</label>
              <input
                id="CL_BALANCE"
                type="number"
                step="any"
                name="CL_BALANCE"
                placeholder="0.00"
                value={formData.CL_BALANCE}
                onChange={handleChange}
              />
            </div>

            {/* GST No */}
            <div className="update_ledger_field">
              <label htmlFor="GSTNO">GST Number</label>
              <input
                id="GSTNO"
                type="text"
                name="GSTNO"
                maxLength={15}
                placeholder="15-character GSTIN"
                value={formData.GSTNO}
                onChange={handleChange}
              />
            </div>

            {/* PAN No */}
            <div className="update_ledger_field">
              <label htmlFor="PANNO">PAN Number</label>
              <input
                id="PANNO"
                type="text"
                name="PANNO"
                maxLength={10}
                placeholder="e.g. ABCDE1234F"
                value={formData.PANNO}
                onChange={handleChange}
              />
            </div>

            {/* Mobile */}
            <div className="update_ledger_field">
              <label htmlFor="MOBILE">Mobile Number</label>
              <input
                id="MOBILE"
                type="text"
                name="MOBILE"
                placeholder="10-digit mobile"
                value={formData.MOBILE}
                onChange={handleChange}
              />
            </div>

            {/* Phone */}
            <div className="update_ledger_field">
              <label htmlFor="PHONE">Phone Number</label>
              <input
                id="PHONE"
                type="text"
                name="PHONE"
                placeholder="Landline / Alt Phone"
                value={formData.PHONE}
                onChange={handleChange}
              />
            </div>

            {/* Email */}
            <div className="update_ledger_field">
              <label htmlFor="EMAIL">Email Address</label>
              <input
                id="EMAIL"
                type="email"
                name="EMAIL"
                placeholder="name@example.com"
                value={formData.EMAIL}
                onChange={handleChange}
              />
            </div>

            {/* Address 1 */}
            <div className="update_ledger_field full_width">
              <label htmlFor="ADDRESS1">Address Line 1</label>
              <textarea
                id="ADDRESS1"
                name="ADDRESS1"
                rows="2"
                placeholder="Street address, building, area..."
                value={formData.ADDRESS1}
                onChange={handleChange}
              />
            </div>

            {/* Address 2 */}
            <div className="update_ledger_field full_width">
              <label htmlFor="ADDRESS2">Address Line 2</label>
              <textarea
                id="ADDRESS2"
                name="ADDRESS2"
                rows="2"
                placeholder="City, District, Pincode..."
                value={formData.ADDRESS2}
                onChange={handleChange}
              />
            </div>
          </div>

          {/* Action Buttons */}
          <div className="update_ledger_actions">
            <Link to="/ShowLedger" className="update_ledger_cancel_btn">
              Cancel
            </Link>
            <button
              type="submit"
              className="update_ledger_submit_btn"
              disabled={loading}
            >
              {loading ? "Saving..." : "Save Ledger"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default AddLedgerMaster;